import {
  ADMIN_ABILITY_DEFINITION,
  type AdminAbility,
  type AdminAbilityRecord,
  type AdminRole,
  type AdminSubject,
  ROLE_ABILITIES,
} from "@/server/models/admin-staff/admin-staff-constants";
import { logger } from "@/utils/logger";
import { Ability, AbilityBuilder } from "@casl/ability";

// Subject→Action のマップを [Action, Subject] タプルのユニオンに変換するユーティリティ型
type AbilityToTuple<T extends Record<PropertyKey, PropertyKey>> = {
  [K in keyof T]: T[K] extends infer V
    ? V extends any
      ? [V, K]
      : never
    : never;
}[keyof T];

type AdminAbilityMap = {
  [Sub in AdminSubject]: (typeof ADMIN_ABILITY_DEFINITION)[Sub][number];
};

type AdminAbilityTuple = AbilityToTuple<AdminAbilityMap>;

export type AdminAbilityInstance = Ability<AdminAbilityTuple>;

/**
 * subjectごとに許可されたactionのみを抽出する
 */
export type SubjectActions<Sub extends AdminSubject> = Extract<
  AdminAbilityTuple,
  [any, Sub]
>[0];

/**
 * 既知のSubjectかどうかを判定する型ガード
 */
function isKnownSubject(subject: string): subject is AdminSubject {
  return Object.prototype.hasOwnProperty.call(
    ADMIN_ABILITY_DEFINITION,
    subject,
  );
}

/**
 * 既知のActionかどうかを判定する型ガード
 */
function isKnownAction(
  subject: AdminSubject,
  action: string,
): action is AdminAbilityMap[typeof subject] {
  const validActions = ADMIN_ABILITY_DEFINITION[
    subject
  ] as ReadonlyArray<string>;
  return validActions.includes(action);
}

/**
 * 既知のロールかどうかを判定する型ガード
 */
function isKnownRole(role: string): role is AdminRole {
  return Object.prototype.hasOwnProperty.call(ROLE_ABILITIES, role);
}

/**
 * ロールのリストを ability のリストに展開する
 *
 * 有効な権限は保有する全ロールの ability の和集合で決まるため、単純に連結する。
 * 未知のロールは無視する（fail closed）。
 * 移行用ロールをコード側の定義から外したあとにDBへ直接INSERTされても、権限が復活しないようにするため。
 */
export function getAbilitiesForRoles(
  roles: ReadonlyArray<string>,
): ReadonlyArray<AdminAbility> {
  return roles.flatMap((role) => {
    if (!isKnownRole(role)) {
      logger.warn({ role }, "getAbilitiesForRoles: unknown role, skipping");
      return [];
    }
    return ROLE_ABILITIES[role];
  });
}

/**
 * AbilityリストからAbilityインスタンスを構築する
 *
 * 未知の subject / action は無視する（fail closed）
 */
export function buildAdminAbility(
  abilities: ReadonlyArray<AdminAbilityRecord>,
): AdminAbilityInstance {
  const { can, build } = new AbilityBuilder<AdminAbilityInstance>(Ability);

  for (const ability of abilities) {
    const { subject, action } = ability;

    if (!isKnownSubject(subject)) {
      logger.warn(
        { subject, action },
        "buildAdminAbility: unknown subject, skipping",
      );
      continue;
    }

    if (!isKnownAction(subject, action)) {
      logger.warn(
        { subject, action },
        "buildAdminAbility: unknown action for subject, skipping",
      );
      continue;
    }

    can(action, subject);
  }

  return build();
}

export const checkAdminAbility = <Sub extends AdminSubject>(
  ability: AdminAbilityInstance,
  subject: Sub,
  action: SubjectActions<Sub>,
): boolean => {
  // CASLのcanは (action, subject) の有効な組ごとのオーバーロードとして型付けされており、
  // subjectがジェネリクスのままだと組を1つに解決できずエラーになる。
  // 引数の型は呼び出し側で保証済みなので、ここでのみ型を緩める
  const can = ability.can.bind(ability) as (
    action: string,
    subject: string,
  ) => boolean;
  return can(action, subject);
};
